// hooks/usePreProductionPlan.ts
import { useState, useEffect } from 'react';
import { db } from '../firebase';
import {
    collection,
    query,
    where,
    onSnapshot,
    orderBy,
    Timestamp,
} from 'firebase/firestore';

// ─── Interfaces ───────────────────────────────────────────────────────────────

export interface MenuItemTags {
    brand?: string[];
    class?: string[];
    cuisine?: string[];
    [key: string]: string[] | undefined;
}

export interface CustomMenuItem {
    category: string;
    displayName: string;
    isCustomMenuItem: boolean;
    itemId: string;
    itemType: string;
    price: number;
    productId: string;
    quantity: number;
    skuId: string;
    tags: MenuItemTags;
}

export interface MenuSection {
    displayName: string;
    menuItems: CustomMenuItem[];
}

export interface CustomMenu {
    displayName: string;
    itemId: string;
    price: number;
    productId: string;
    quantity: number;
    sections: MenuSection[];
}

export interface PreProductionPlan {
    id: string;
    createdAt: Timestamp | any;
    updatedAt: Timestamp | any;
    customMenus: CustomMenu[];
    orderRequestId: string;
    restaurantId: string;
    targetDate: Timestamp | any;
}

export interface PreProductionPlanData {
    plans: PreProductionPlan[];
    loading: boolean;
    error: Error | null;
}

interface UsePreProductionPlanProps {
    restaurantId: string;
    /** ISO date string, e.g. "2026-03-31". Defaults to today. */
    targetDate?: string;
    skip?: boolean;
}

// ─── Helper ───────────────────────────────────────────────────────────────────

const buildDayRange = (dateStr: string): { start: Timestamp; end: Timestamp } => {
    const start = new Date(dateStr);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + 1);
    return {
        start: Timestamp.fromDate(start),
        end: Timestamp.fromDate(end),
    };
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const usePreProductionPlan = ({
    restaurantId,
    targetDate,
    skip = false,
}: UsePreProductionPlanProps): PreProductionPlanData => {
    const [plans, setPlans] = useState<PreProductionPlan[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        if (!restaurantId || skip) {
            setPlans([]);
            setLoading(false);
            setError(null);
            return;
        }

        setLoading(true);
        setError(null);

        const dateToUse = targetDate || new Date().toISOString().split('T')[0];
        const { start, end } = buildDayRange(dateToUse);

        const plansRef = collection(db, 'Restaurants', restaurantId, 'preProductionPlan');

        const q = query(
            plansRef,
            where('targetDate', '>=', start),
            where('targetDate', '<', end),
            orderBy('targetDate', 'asc'),
        );

        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                const result: PreProductionPlan[] = snapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...(doc.data() as Omit<PreProductionPlan, 'id'>),
                }));
                setPlans(result);
                setLoading(false);
                setError(null);
            },
            (err) => {
                console.error('❌ Error fetching preProductionPlan:', err);
                setError(err as Error);
                setLoading(false);
            },
        );

        return () => unsubscribe();
    }, [restaurantId, targetDate, skip]);

    return { plans, loading, error };
};